import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Contact from "../components/Contact";
import BioInfo from "../components/BioInfo.js";
import Exhibitions from "../components/Exhibitions";
import BackButton from "../components/BackButton";

import soloExhibitions from "./../data/soloExhibitions_Data";
import groupExhibitions from "./../data/groupExhibitions_Data";

import artistPicture from "../images/bio/tyroneLaynePainting2025.webp";
import artistPortrait from "../images/bio/tyroneLayneStudio2025.webp";
import easternSuburbs from "../images/bio/easternsuburbs_install.webp";
import toafBK from "../images/bio/2toafBK2023.webp";
import studio from "../images/bio/tyroneLayneStudio2025a.webp";
import studio2 from "../images/bio/tyroneLayneStudio2025b.webp";

// Background images in scroll order:
//   0  artistPicture   — hero + contact card
//   1  artistPortrait  — artist statement card
//   2  easternSuburbs  — solo exhibitions card
//   3  toafBK          — group exhibitions card  (new image)
//   4  studio          — subscribe card

function Biography() {
  const [isHovered, setIsHovered] = useState(false);
  const [activeBg, setActiveBg] = useState(artistPicture);
  const [scrollerInView, setScrollerInView] = useState(false);
  const [closingInView, setClosingInView] = useState(false);
  const bgVisible = scrollerInView && !closingInView;

  const scrollerRef = useRef(null);
  const closingRef = useRef(null);
  const sectionRefs = useRef([]);

  // Preload all background images so swaps are instant.
  useEffect(() => {
    [artistPicture, artistPortrait, easternSuburbs, toafBK, studio].forEach(
      (src) => {
        const img = new Image();
        img.src = src;
      }
    );
  }, []);

  // Show fixed bg while the parallax scroller is in view.
  useEffect(() => {
    const target = scrollerRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      ([entry]) => setScrollerInView(entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  // Pre-emptively hide the bg as the closing section approaches,
  // preventing the iOS address-bar flicker.
  useEffect(() => {
    const target = closingRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      ([entry]) => setClosingInView(entry.isIntersecting),
      { rootMargin: "0px 0px 200px 0px", threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  // Swap the bg to whichever section is in the middle band of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio)
            best = entry;
        });
        if (best) {
          const next = best.target.dataset.bg;
          if (next) setActiveBg(next);
        }
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Helmet>
        <title>Tyrone Layne | About</title>
        <meta property='og:title' content='Tyrone Layne | About' />
        <meta property='og:image' content={artistPicture} />
        <meta
          property='og:description'
          content="Tyrone's oil on canvas peoplescapes are fresh and sensitively drawn observations
              of human behaviour, layered with intricate exchanges between characters in the background
              whilst making confidently broad statements about people and culture."
        />
        <link rel='canonical' href='https://tyronelayne.com/biography' />
        {/* Preload the hero background so it's fetched before JS sets backgroundImage */}
        <link rel='preload' as='image' href={artistPicture} fetchpriority='high' />
      </Helmet>

      {/* Hero as <img> — browser-discoverable LCP element for section 1.
          Fades out once the user scrolls to section 2 and the CSS bg takes over. */}
      <img
        src={artistPicture}
        alt=''
        aria-hidden='true'
        fetchpriority='high'
        decoding='async'
        style={{
          position: 'fixed',
          top: 0, left: 0,
          width: '100vw', height: '100vh',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 0,
          opacity: activeBg === artistPicture && bgVisible ? 1 : 0,
          transition: 'opacity 400ms ease',
          pointerEvents: 'none',
        }}
      />

      {/* Sections 2+ — CSS background crossfades as the user scrolls. */}
      <div
        className={`vrPxFixedBg${activeBg !== artistPicture && bgVisible ? " vrPxFixedBg--visible" : ""}${activeBg === artistPortrait ? " vrPxFixedBg--top" : ""}`}
        style={{ backgroundImage: `url(${activeBg})` }}
        role='presentation'
        aria-hidden='true'
      />

      <div className='vrPxScroller' ref={scrollerRef}>

        {/* ── HERO ──────────────────────────────────────────────────────
            Transparent 100vh gap. Title and scroll hint sit over the
            first background image (artistPicture). */}
        <section
          className='vrPxSection'
          data-bg={artistPicture}
          ref={(el) => { sectionRefs.current[0] = el; }}
        >
          <div className='homeHeroGap'>
            <div className='homeHeroTitleBlock'>
              <h1 className='homeHeroName'>TYRONE LAYNE</h1>
              <h1 className='homeHeroPainter'>ABOUT</h1>
            </div>
            <div className='vrPxScrollHint'>
              <span className='vrPxScrollHintText'>Scroll down</span>
              <span className='vrPxScrollHintArrow' aria-hidden='true'>
                &#x2193;
              </span>
            </div>
          </div>
        </section>

        {/* ── CARD 1 · Contact & bio info ───────────────────────────────
            No gap before this card — it slides up directly from beneath
            the hero title as the user scrolls. */}
        <section className='vrPxSection'>
          <article className='vrPxFullCard'>
            <div className='vrPxFullCardInner aboutContactInner'>
              <Contact />
              <BioInfo />
            </div>
          </article>
        </section>

        {/* ── CARD 2 · Artist Statement ─────────────────────────────────
            Background swaps to artistPortrait as this gap scrolls into
            the middle band of the viewport. */}
        <section
          className='vrPxSection'
          data-bg={artistPortrait}
          ref={(el) => { sectionRefs.current[1] = el; }}
        >
          <div className='vrPxGap' aria-hidden='true' />
          <article className='vrPxFullCard'>
            <div className='vrPxFullCardInner'>
              <p className='aboutCardEyebrow'>
                Artist Statement &mdash; Peoplescapes
              </p>
              <p className='aboutStatementPara'>
                My Peoplescapes are observational paintings of public life: scenes built from the people,
                gestures, and small incidents I encounter in a particular place and moment in time. Each
                work begins as a kind of social field study &mdash; a way of looking closely at a city,
                a neighbourhood, a beach, a park &mdash; and ends as a portrait of a culture rather than
                of any one person.
              </p>
              <p className='aboutStatementPara'>
                I'm drawn to the collective energy of shared spaces. A street, a shoreline, a stretch of
                pavement outside a transit stop &mdash; these are stages where contemporary life plays
                out in miniature. By gathering figures from my own observations alongside what filters
                in through news, literature, television, and social media, I try to capture the texture
                of a particular era and place: what people wear, what they carry, how they move past one
                another, what they're absorbed in. The figures become a cast of universal characters
                &mdash; the dog walker, the cyclist, the parent with a stroller, the worker on a break
                &mdash; and together they tell a wider story about who we are right now.
              </p>
              <p className='aboutStatementPara'>
                The work lives in the details. I'm interested in the moments most paintings leave out:
                someone bending to tie a shoelace, a small exchange near a bus stop, a child trailing
                behind a parent, a courier checking a phone. These mundane fragments are, to me, where
                real life happens. I leave the background as a flat, unmarked field so that nothing
                competes with these encounters. The empty ground isolates each figure and each small
                relationship, asking the viewer to slow down and read the scene one human moment at a
                time.
              </p>
              <p className='aboutStatementPara' style={{ marginBottom: 0 }}>
                At a distance, the paintings resolve into a single composition. Figures cluster and
                disperse across the canvas in a deliberate rhythm, weighted and spaced so the whole
                reads as one balanced image. Up close, that image dissolves back into hundreds of
                individual stories. My hope is that viewers move between those two ways of seeing
                &mdash; the panoramic and the intimate &mdash; and come away with a renewed attention
                to the ordinary world they walk through every day.
              </p>
            </div>
          </article>
        </section>

        {/* ── CARD 3 · Solo Exhibitions ─────────────────────────────────
            Background swaps to easternSuburbs installation shot. */}
        <section
          className='vrPxSection'
          data-bg={easternSuburbs}
          ref={(el) => { sectionRefs.current[2] = el; }}
        >
          <div className='vrPxGap' aria-hidden='true' />
          <article className='vrPxFullCard'>
            <div className='vrPxFullCardInner'>
              <h2>Solo Exhibitions:</h2>
              <Exhibitions exhibitions={soloExhibitions} variant='timeline' />
            </div>
          </article>
        </section>

        {/* ── CARD 4 · Group Exhibitions ────────────────────────────────
            Background swaps to 2toafBK2023.jpg (new image). */}
        <section
          className='vrPxSection'
          data-bg={toafBK}
          ref={(el) => { sectionRefs.current[3] = el; }}
        >
          <div className='vrPxGap' aria-hidden='true' />
          <article className='vrPxFullCard'>
            <div className='vrPxFullCardInner'>
              <h2>Selected Group Exhibitions &amp; Art Fairs:</h2>
              <Exhibitions exhibitions={groupExhibitions} variant='timeline' />
            </div>
          </article>
        </section>

        {/* ── CARD 5 · Subscribe ────────────────────────────────────────
            Background swaps to studio shot. */}
        <section
          className='vrPxSection'
          data-bg={studio}
          ref={(el) => { sectionRefs.current[4] = el; }}
        >
          <div className='vrPxGap' aria-hidden='true' />
          <article className='vrPxFullCard'>
            <div className='vrPxFullCardInner aboutSubscribeInner'>
              <figure className='aboutSubscribeFigure'>
                <img
                  src={studio2}
                  alt="Detail of Tyrone Layne's studio workspace, 2025."
                  className='aboutSubscribeImage'
                  loading='lazy'
                />
              </figure>
              <p className='aboutCardEyebrow'>Stay in touch</p>
              <Link
                to='/newsletter'
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  display: "inline-block",
                  padding: "14px 32px",
                  backgroundColor: isHovered ? "#000" : "transparent",
                  color: isHovered ? "#fff" : "#000",
                  border: "1px solid #000",
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  transition: "background-color 0.2s ease, color 0.2s ease",
                }}
              >
                Subscribe to Tyrone's mailing list
              </Link>
            </div>
          </article>
        </section>

      </div>{/* end vrPxScroller */}

      {/* ── GO BACK ───────────────────────────────────────────────────
          Sits outside the scroller; entering the viewport hides the
          fixed background. */}
      <div ref={closingRef}>
        <BackButton />
      </div>

    </div>
  );
}

export default Biography;
